import React, { useCallback, useMemo, useState } from 'react';
import { DataTable, DropdownMenu, Search, Text, SparkIcon } from '@spark';

const PAGE_SIZE = 10;

/**
 * "Recent Payloads" section: filter by output label + search toolbar, data table, pagination.
 *
 * Receives ``payloads`` shaped by the backend:
 *   { id, timestamp, input, output, confidence, latencyMs, status }
 * where ``input`` and ``output`` are human-readable previews already extracted
 * server-side from the raw request/response bodies.
 */
const ExpandableInput = ({ value }) => {
    const [expanded, setExpanded] = useState(false);
    if (!value) return '—';
    return (
        <div
            className={`recent-payloads__input-cell ${expanded ? 'recent-payloads__input-cell--expanded' : ''}`}
            onClick={() => setExpanded((prev) => !prev)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded((prev) => !prev); }}
        >
            {value}
        </div>
    );
};

const RecentPayloadsSection = ({ payloads = [] }) => {
    const [filterLabel, setFilterLabel] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);

    // Build filter options dynamically from distinct output labels
    const filterOptions = useMemo(() => {
        const labels = [...new Set(payloads.map((r) => r.output).filter(Boolean))].sort();
        return ['All', ...labels];
    }, [payloads]);

    const filtered = useMemo(() => {
        let rows = payloads;
        if (filterLabel !== 'All') {
            rows = rows.filter((r) => r.output === filterLabel);
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            rows = rows.filter(
                (r) =>
                    r.id?.toLowerCase().includes(q) ||
                    r.input?.toLowerCase().includes(q) ||
                    r.output?.toLowerCase().includes(q)
            );
        }
        return rows;
    }, [payloads, filterLabel, searchQuery]);

    const columns = useMemo(() => [
        {
            key: 'id',
            dataIndex: 'id',
            title: 'REQUEST ID',
            width: 120,
        },
        {
            key: 'timestamp',
            dataIndex: 'timestamp',
            title: 'TIMESTAMP',
            width: 220,
        },
        {
            key: 'input',
            dataIndex: 'input',
            title: 'INPUT',
            render: (val) => <ExpandableInput value={val} />,
        },
        {
            key: 'output',
            dataIndex: 'output',
            title: 'OUTPUT',
            width: 140,
            render: (val) => val || '—',
        },
        {
            key: 'confidence',
            dataIndex: 'confidence',
            title: 'CONFIDENCE',
            width: 120,
            render: (val) => (val != null ? `${val}%` : '—'),
        },
        {
            key: 'latencyMs',
            dataIndex: 'latencyMs',
            title: 'LATENCY',
            width: 100,
            render: (val) => (val != null ? `${val}ms` : '—'),
        },
        {
            key: 'actions',
            dataIndex: 'id',
            title: '',
            width: 56,
            render: () => (
                <button
                    type="button"
                    className="recent-payloads__action-btn"
                    aria-label="More actions"
                >
                    <SparkIcon slug="dots" size="small" />
                </button>
            ),
        },
    ], []);

    const filterItems = filterOptions.map((label) => ({ label, showPrefixIcon: false }));
    const activeIdx = filterOptions.indexOf(filterLabel);

    return (
        <div className="recent-payloads">
            <Text variant="body-semibold" as="h3">Recent Payloads</Text>

            <div className="recent-payloads__toolbar">
                <div className="recent-payloads__filter">
                    <DropdownMenu
                        size="medium"
                        showLabel={false}
                        showPrefixIcon={filterLabel === 'All'}
                        prefixIcon={<SparkIcon slug="filter-lines" size="small" />}
                        placeholder="Filter"
                        text={filterLabel === 'All' ? undefined : filterLabel}
                        value={filterLabel === 'All' ? undefined : filterLabel}
                        items={filterItems}
                        onSelect={(_, idx) => {
                            setFilterLabel(filterOptions[idx]);
                            setPage(1);
                        }}
                    />
                </div>
                <div className="recent-payloads__search">
                    <Search
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setPage(1);
                        }}
                        placeholder="Search"
                        size="medium"
                    />
                </div>
            </div>

            <DataTable
                columns={columns}
                dataSource={filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)}
                rowKey="id"
                pagination={
                    filtered.length > PAGE_SIZE
                        ? {
                            current: page,
                            pageSize: PAGE_SIZE,
                            total: filtered.length,
                            onChange: setPage,
                        }
                        : null
                }
                emptyText="No payloads match the current filter."
            />
        </div>
    );
};

export default RecentPayloadsSection;
